// src/components/learner/PortfolioItemView.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { convertToDate, formatDate } from '../../utils/dateUtils';
import '../../styles/portfolio.css';

const PortfolioItemView = () => {
  const { assignmentId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [assignment, setAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPortfolioItem = async () => {
      if (!currentUser || !assignmentId) {
        setError("Missing user or assignment information");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const db = getFirestore();

        // Fetch assignment
        const assignmentDoc = await getDoc(doc(db, "assignments", assignmentId));
        if (!assignmentDoc.exists()) {
          setError("Assignment not found");
          setLoading(false);
          return;
        }

        const assignmentData = {
          id: assignmentDoc.id,
          ...assignmentDoc.data()
        };
        assignmentData.startDate = convertToDate(assignmentData.startDate);
        assignmentData.endDate = convertToDate(assignmentData.endDate);
        setAssignment(assignmentData);

        // Fetch most recent submitted/updated submission
        const submissionDocs = await getDocs(
          query(
            collection(db, "submissions"),
            where("learnerId", "==", currentUser.uid),
            where("assignmentId", "==", assignmentId),
            where("submissionStatus", "in", ["submitted", "updated"])
          )
        );

        if (submissionDocs.empty) {
          setLoading(false);
          return;
        }

        // Find the latest by updatedAt
        let latestSubmission = null;
        let latestDate = new Date(0);

        submissionDocs.forEach(docSnap => {
          const data = docSnap.data();
          const updatedAt = convertToDate(data.updatedAt) || new Date(0);
          if (updatedAt > latestDate) {
            latestDate = updatedAt;
            latestSubmission = {
              id: docSnap.id,
              ...data,
              createdAt: convertToDate(data.createdAt),
              updatedAt: convertToDate(data.updatedAt)
            };
          }
        });

        if (!latestSubmission) {
          setLoading(false);
          return;
        }

        // Fetch feedback if available
        try {
          const feedbackDocs = await getDocs(
            query(collection(db, "feedback"), where("submissionId", "==", latestSubmission.id))
          );

          if (!feedbackDocs.empty) {
            const feedbackData = feedbackDocs.docs[0].data();
            latestSubmission.mentorFeedback = {
              positivePoints: feedbackData.positivePoints || '',
              improvementAreas: feedbackData.improvementAreas || '',
              content: feedbackData.content || ''
            };
          }
        } catch {
          // Feedback is optional — continue without it
        }

        setSubmission(latestSubmission);
      } catch (err) {
        console.error("Error fetching portfolio item:", err);
        setError("Failed to load assignment data");
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolioItem();
  }, [currentUser, assignmentId]);

  const handleBackToPortfolio = () => {
    navigate('/learner', { state: { activeTab: 'portfolio' } });
  };

  const renderFiles = (files) => {
    if (!files?.length) return null;
    return (
      <div className="submission-files">
        <h3>Submitted Files</h3>
        <ul className="file-list">
          {files.map((file, index) => (
            <li key={index} className="file-item">
              <div className="file-info">
                <span className="file-name">{file.name || `File ${index + 1}`}</span>
                <span className="file-type">{file.type || ''}</span>
              </div>
              <div className="file-actions">
                <a href={file.url} target="_blank" rel="noopener noreferrer" className="view-button">View</a>
                <a href={file.url} download className="download-button">Download</a>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderFeedback = () => {
    if (!submission?.mentorFeedback) return null;
    const { positivePoints, improvementAreas, content } = submission.mentorFeedback;

    return (
      <div className="feedback-container">
        <h3>Mentor Feedback</h3>
        {positivePoints && (
          <div className="feedback-section positive">
            <h4>What Was Good</h4>
            <div className="feedback-content">{positivePoints}</div>
          </div>
        )}
        {improvementAreas && (
          <div className="feedback-section improvement">
            <h4>Areas to Improve</h4>
            <div className="feedback-content">{improvementAreas}</div>
          </div>
        )}
        {content && !positivePoints && !improvementAreas && (
          <div className="feedback-section general">
            <h4>Feedback</h4>
            <div className="feedback-content">{content}</div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="portfolio-item-view loading">
        <div className="loading-spinner"></div>
        <p>Loading assignment details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portfolio-item-view error">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={handleBackToPortfolio} className="back-button">Back to Portfolio</button>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="portfolio-item-view not-found">
        <div className="not-found-message">
          <h3>Assignment Not Found</h3>
          <p>The requested assignment could not be found.</p>
          <button onClick={handleBackToPortfolio} className="back-button">Back to Portfolio</button>
        </div>
      </div>
    );
  }

  let statusLabel = 'Not Submitted';
  let statusClass = 'not-submitted';
  if (submission) {
    if (submission.reviewStatus === 'reviewed') { statusLabel = 'Reviewed'; statusClass = 'reviewed'; }
    else if (submission.submissionStatus === 'updated') { statusLabel = 'Updated'; statusClass = 'updated'; }
    else if (submission.submissionStatus === 'submitted') { statusLabel = 'Submitted'; statusClass = 'submitted'; }
  }

  return (
    <div className="portfolio-item-view">
      <div className="portfolio-nav">
        <button onClick={handleBackToPortfolio} className="back-button">
          &larr; Back to Portfolio
        </button>
        <div className={`submission-status ${statusClass}`}>{statusLabel}</div>
      </div>

      <div className="assignment-details">
        <h2>{assignment.title}</h2>

        <div className="assignment-dates">
          <div className="date-item">
            <span className="date-label">Started:</span>
            <span className="date-value">{formatDate(assignment.startDate, 'long', 'Not set')}</span>
          </div>
          <div className="date-item">
            <span className="date-label">Due Date:</span>
            <span className="date-value">{formatDate(assignment.endDate, 'long', 'Not set')}</span>
          </div>
          {submission?.updatedAt && (
            <div className="date-item">
              <span className="date-label">Submitted:</span>
              <span className="date-value">{formatDate(submission.updatedAt, 'long')}</span>
            </div>
          )}
        </div>

        <div className="assignment-instructions">
          <h3>Assignment Instructions</h3>
          <div className="instruction-content">
            {assignment.instruction || "No instructions provided."}
          </div>
        </div>
      </div>

      {submission ? (
        <div className="submission-container">
          <h3>Your Submission</h3>
          {submission.content && (
            <div className="submission-content">{submission.content}</div>
          )}
          {renderFiles(submission.fileUrls)}
          {renderFeedback()}
        </div>
      ) : (
        <div className="no-submission">
          <p>You did not submit anything for this assignment.</p>
        </div>
      )}
    </div>
  );
};

export default PortfolioItemView;